// src/pages/Dashboard.tsx
// Analytics Dashboard — accessed from the landing page.
// UPDATED: March 11, 2026
//
// FIXES:
//   1. Total AR KPI card now sourced from arData (AR aging endpoint) for consistency
//   2. Profit Margin displays correctly as percentage
//   3. YTD label is dynamic (current year)
//   4. 12-Week Trend chart uses new /api/sales/trend endpoint (top 5 reps + Other)
//   5. Removed dependency on weeks[] from SalesRepSummary

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, TrendingDown, DollarSign, Package, AlertCircle,
  Search, Download, ChevronDown, Calendar, RefreshCw, Check,
  ArrowLeft, BarChart3,
} from 'lucide-react';
import {
  ComposedChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useAuthContext } from '../hooks/useAuth';
import { api, downloadFile } from '../services/api';
import type { SalesRepSummary, ARSummary, Filters, TrendDataPoint } from '../types';
// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface DashboardProps {
  onBack?: () => void;
  onNavigate?: (page: 'landing' | 'analytics' | 'detailAnalytics' | 'powerbi' | 'schedules') => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// Utility Functions
// ═══════════════════════════════════════════════════════════════════════════

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);
const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const currentYear = new Date().getFullYear();

// Build week labels W-12 through W-1
const weekLabels = Array.from({ length: 12 }, (_, i) => `W-${12 - i}`);

// Helper: get the Monday of the ISO week for a given date
function getWeekKey(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getUTCDay();
  const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(d);
  monday.setUTCDate(diff);
  return monday.toISOString().slice(0, 10);
}

// Build an array of 12 week-start dates (Mondays) ending with the current week
function getLast12WeekStarts(): string[] {
  const today = new Date();
  const currentMonday = new Date(today);
  const day = currentMonday.getUTCDay();
  const diff = currentMonday.getUTCDate() - day + (day === 0 ? -6 : 1);
  currentMonday.setUTCDate(diff);

  const weeks: string[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(currentMonday);
    d.setUTCDate(d.getUTCDate() - i * 7);
    weeks.push(d.toISOString().slice(0, 10));
  }
  return weeks;
}

// ═══════════════════════════════════════════════════════════════════════════
// Stat Card
// ═══════════════════════════════════════════════════════════════════════════

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue' }: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<{ size?: number }>;
  trend?: 'up' | 'down';
  trendValue?: string;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30',
    emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/30',
    red: 'from-red-500/20 to-red-600/5 border-red-500/30',
  };
  const iconColorMap: Record<string, string> = {
    blue: 'text-blue-400 bg-blue-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    red: 'text-red-400 bg-red-500/10',
  };

  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-5 ${colorMap[color] || colorMap.blue}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-xl ${iconColorMap[color] || iconColorMap.blue}`}>
          <Icon size={18} />
        </div>
        {trend && trendValue && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
      <p className="text-sm text-slate-400 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AR Aging Chart
// ═══════════════════════════════════════════════════════════════════════════

function ARAgingChart({ data }: { data?: ARSummary }) {
  if (!data) return null;

  const chartData = data.buckets.map(b => ({
    name: b.label,
    value: b.amount,
    label: b.label,
    amount: b.amount,
    percentage: b.percentage,
    color: b.color || '#94a3b8',
  }));

  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm p-5">
      <h3 className="text-lg font-semibold text-white mb-1">AR Aging</h3>
      <p className="text-2xl font-bold text-white mb-3">{formatCurrency(data.totalAR)}</p>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              dataKey="value"
              stroke="none"
            >
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
              formatter={(value) => [formatCurrency(Number(value)), '']}
            />
            <Legend
              formatter={(value) => <span className="text-slate-400 text-xs">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Weekly Trend Chart — NOW uses /api/sales/trend data
// ═══════════════════════════════════════════════════════════════════════════

function WeeklyTrendChart({ trendData, selectedSalesman, salesData }: {
  trendData?: TrendDataPoint[];
  selectedSalesman: string | null;
  salesData?: SalesRepSummary[];
}) {
  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0) return [];

    // Get top 5 reps by total sales (from salesData if available)
    const topReps = new Set<string>();
    if (salesData && salesData.length > 0) {
      const sorted = [...salesData]
        .filter(r => r.salesman !== 'UNASSIGNED')
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 5);
      sorted.forEach(r => topReps.add(r.salesman));
    } else {
      // Fallback: derive from trend data
      const repTotals: Record<string, number> = {};
      trendData.forEach(p => {
        if (p.rep !== 'UNASSIGNED') {
          repTotals[p.rep] = (repTotals[p.rep] || 0) + p.sales;
        }
      });
      Object.entries(repTotals)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .forEach(([rep]) => topReps.add(rep));
    }

    // Build week starts
    const weekStarts = getLast12WeekStarts();

    // Aggregate daily data into weekly buckets per rep
    const weeklyByRep: Record<string, Record<string, number>> = {};
    trendData.forEach(point => {
      const rep = topReps.has(point.rep) ? point.rep : null;
      if (!rep) return; // skip UNASSIGNED and non-top reps from chart
      const weekKey = getWeekKey(point.date);
      if (!weeklyByRep[rep]) weeklyByRep[rep] = {};
      weeklyByRep[rep][weekKey] = (weeklyByRep[rep][weekKey] || 0) + point.sales;
    });

    // Build chart data array
    return weekStarts.map((weekStart, idx) => {
      const entry: Record<string, string | number> = { week: weekLabels[idx] };
      topReps.forEach(rep => {
        entry[rep] = weeklyByRep[rep]?.[weekStart] || 0;
      });
      return entry;
    });
  }, [trendData, salesData]);

  // Get rep names for chart areas
  const repNames = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    return Object.keys(chartData[0]).filter(k => k !== 'week');
  }, [chartData]);

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm p-5">
      <h3 className="text-lg font-semibold text-white mb-4">12-Week Sales Trend</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
              labelStyle={{ color: '#f1f5f9' }}
              formatter={(value) => [formatCurrency(Number(value)), '']}
            />
            {repNames.map((rep, idx) => (
              <Area
                key={rep}
                type="monotone"
                dataKey={rep}
                stroke={colors[idx % colors.length]}
                fill={colors[idx % colors.length]}
                fillOpacity={selectedSalesman === rep ? 0.3 : 0.05}
                strokeWidth={selectedSalesman === rep ? 3 : 1.5}
                strokeOpacity={selectedSalesman ? (selectedSalesman === rep ? 1 : 0.3) : 1}
              />
            ))}
            <Legend
              formatter={(value) => <span className="text-slate-400 text-xs">{value}</span>}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sales Table
// ═══════════════════════════════════════════════════════════════════════════

function SalesTable({ data, onRowClick, selectedSalesman }: {
  data: SalesRepSummary[];
  onRowClick: (salesman: string | null) => void;
  selectedSalesman: string | null;
}) {
  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Rep</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Sales</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Profit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Margin</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Invoices</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">AR Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={row.salesman}
                onClick={() => onRowClick(selectedSalesman === row.salesman ? null : row.salesman)}
                className={`border-b border-slate-800/50 cursor-pointer transition-colors ${
                  selectedSalesman === row.salesman
                    ? 'bg-blue-500/10'
                    : 'hover:bg-slate-700/30'
                }`}
              >
                <td className="px-4 py-3 font-medium text-white">{row.salesman}</td>
                <td className="px-4 py-3 text-slate-300">{formatCurrency(row.sales)}</td>
                <td className="px-4 py-3 text-emerald-400">{formatCurrency(row.profit)}</td>
                <td className="px-4 py-3 text-slate-300">{formatPercent(row.margin)}</td>
                <td className="px-4 py-3 text-slate-300">{formatNumber(row.invoices)}</td>
                <td className="px-4 py-3 text-amber-400">{formatCurrency(row.arBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Dashboard Component
// ═══════════════════════════════════════════════════════════════════════════

export default function Dashboard({ onBack, onNavigate }: DashboardProps) {
  const { user, branding, hasFeature, isSalesRep, claims } = useAuthContext();
  const [filters, setFilters] = useState<Filters>({});
  const [selectedSalesman, setSelectedSalesman] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const effectiveFilters: Filters = useMemo(() => {
    if (isSalesRep && claims?.salesman_code) {
      return { ...filters, salesman: claims.salesman_code };
    }
    return filters;
  }, [filters, isSalesRep, claims?.salesman_code]);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboardStats', effectiveFilters],
    queryFn: () => api.getDashboardStats(effectiveFilters),
    retry: 1,
  });

  const { data: salesData, isLoading: salesLoading, refetch: refetchSales } = useQuery({
    queryKey: ['salesByRep', effectiveFilters],
    queryFn: () => api.getSalesByRep(effectiveFilters),
    retry: 1,
  });

  const { data: arData } = useQuery({
    queryKey: ['arAging', effectiveFilters],
    queryFn: () => api.getARaging(effectiveFilters),
    enabled: hasFeature('arAging'),
    retry: 1,
  });

  // NEW: Trend data query
  const { data: trendData } = useQuery({
    queryKey: ['salesTrend', effectiveFilters],
    queryFn: () => api.getSalesTrend(effectiveFilters),
    retry: 1,
  });

  const { data: salesmenList } = useQuery({
    queryKey: ['salesmenList'],
    queryFn: () => api.getSalesmenList(),
    retry: 1,
  });

  const { data: categoriesList } = useQuery({
    queryKey: ['categoriesList'],
    queryFn: () => api.getCategoriesList(),
    retry: 1,
  });

  const { data: hebrewDate } = useQuery({
    queryKey: ['hebrewDate'],
    queryFn: () => api.getHebrewDate(),
    enabled: hasFeature('hebrewCalendar'),
    retry: 1,
  });

  // ── Derived data ─────────────────────────────────────────────────────────

  const filteredSalesData = useMemo(() => {
    if (!salesData) return [];
    if (!searchTerm) return salesData;
    return salesData.filter(s =>
      s.salesman.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [salesData, searchTerm]);

  const totals = useMemo(() => {
    if (!filteredSalesData.length) return { sales: 0, profit: 0, invoices: 0, arBalance: 0 };
    return filteredSalesData.reduce((acc, row) => ({
      sales: acc.sales + (row.sales || 0),
      profit: acc.profit + (row.profit || 0),
      invoices: acc.invoices + (row.invoices || 0),
      arBalance: acc.arBalance + (row.arBalance || 0),
    }), { sales: 0, profit: 0, invoices: 0, arBalance: 0 });
  }, [filteredSalesData]);

  const isLoading = statsLoading || salesLoading;

  const handleRefresh = () => {
    refetchStats();
    refetchSales();
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      const blob = await api.exportReport('sales-by-rep', format, effectiveFilters);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      downloadFile(blob, `sales-by-rep.${ext}`);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                    title="Back to home"
                  >
                    <ArrowLeft size={18} />
                  </button>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-500/20">
                    {branding?.company_name?.charAt(0) || 'D'}
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-white tracking-tight">
                      {branding?.company_name || 'Delldata Reports'}
                    </h1>
                    <p className="text-xs text-slate-500">Analytics Dashboard</p>
                  </div>
                </div>
              </div>

              {/* Right: Hebrew date + user */}
              <div className="flex items-center gap-4">
                {hebrewDate && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <Calendar size={14} className="text-slate-500" />
                    <span className="text-sm text-slate-400">{hebrewDate.gregorian}</span>
                    <span className="text-sm text-slate-500">•</span>
                    <span className="text-sm text-slate-300 font-hebrew">{hebrewDate.hebrew}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                    {(user?.firstName?.charAt(0) || '') + (user?.lastName?.charAt(0) || '') || user?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-white">{user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : user?.name || 'User'}</p>
                    <p className="text-xs text-slate-500">{user?.email || ''}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Sales"
              value={formatCurrency(stats?.totalSales || totals.sales)}
              subtitle={`YTD ${currentYear}`}
              icon={DollarSign}
              color="blue"
              trend={stats?.weeklyChange ? (stats.weeklyChange > 0 ? 'up' : 'down') : undefined}
              trendValue={stats?.weeklyChange ? `${Math.abs(stats.weeklyChange).toFixed(1)}%` : undefined}
            />
            <StatCard
              title="Gross Profit"
              value={formatCurrency(stats?.totalProfit || totals.profit)}
              subtitle={`Margin: ${formatPercent(stats?.profitMargin || (totals.sales ? totals.profit / totals.sales * 100 : 0))}`}
              icon={TrendingUp}
              color="emerald"
            />
            <StatCard
              title="Total AR"
              value={formatCurrency(arData?.totalAR ?? stats?.totalAR ?? totals.arBalance)}
              subtitle="Outstanding balance"
              icon={AlertCircle}
              color="amber"
            />
            <StatCard
              title="Invoices"
              value={formatNumber(stats?.invoiceCount || totals.invoices)}
              subtitle={stats?.customerCount ? `${stats.customerCount} customers` : undefined}
              icon={Package}
              color="blue"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-48">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search reps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            {salesmenList && !isSalesRep && (
              <div className="relative">
                <select
                  value={filters.salesman || ''}
                  onChange={(e) => setFilters(f => ({ ...f, salesman: e.target.value || undefined }))}
                  className="appearance-none px-4 py-2 pr-8 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer"
                >
                  <option value="">All Reps</option>
                  {salesmenList.map(s => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              </div>
            )}

            {categoriesList && (
              <div className="relative">
                <select
                  value={filters.category || ''}
                  onChange={(e) => setFilters(f => ({ ...f, category: e.target.value || undefined }))}
                  className="appearance-none px-4 py-2 pr-8 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer"
                >
                  <option value="">All Categories</option>
                  {categoriesList.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              </div>
            )}

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>

            {hasFeature('exportExcel') && (
              <button
                onClick={() => handleExport('excel')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-all"
              >
                <Download size={14} />
                Export
              </button>
            )}
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <WeeklyTrendChart
                trendData={trendData}
                selectedSalesman={selectedSalesman}
                salesData={salesData}
              />
            </div>
            {hasFeature('arAging') && <ARAgingChart data={arData} />}
          </div>

          {/* Sales Table */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Sales by Representative</h2>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('detailAnalytics')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:text-white hover:bg-blue-600/40 transition-all text-sm"
                >
                  <BarChart3 size={14} />
                  Detail Analytics
                </button>
              )}
            </div>
            {salesLoading ? (
              <div className="flex items-center justify-center h-40">
                <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : (
              <SalesTable
                data={filteredSalesData}
                onRowClick={setSelectedSalesman}
                selectedSalesman={selectedSalesman}
              />
            )}
          </div>

          {/* Footer */}
          <footer className="pt-8 pb-4 border-t border-slate-800/50">
            <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Check size={14} className="text-emerald-500" />
                <span>Data synced • Last updated: {new Date().toLocaleTimeString()}</span>
              </div>
              <span>© {currentYear} {branding?.company_name || 'Delldata Reports'}</span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}